import { createContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { useLocale } from "@/hooks/use-locale";
import { overridesToCatalog, useI18nOverrides } from "@/hooks/use-i18n-overrides";
import { enCatalog, loadChain, translate, type Catalog, type TFunction } from "./catalog";
import { defaultLocale, getDirection, type Locale } from "./config";

export interface I18nContextValue {
  locale: Locale;
  t: TFunction;
}

/**
 * The default is a working English context, not `null` (POS2-1133).
 *
 * English is the architecture's guaranteed floor — bundled statically, available offline, the
 * last rung of every fallback chain — so a component rendered outside the provider should render
 * English, exactly as one rendered inside it does before the German chunk arrives. A throwing
 * context would instead make "no provider" a crash, and would force every existing component
 * test to grow a wrapper for no behavioural gain.
 */
export const I18nContext = createContext<I18nContextValue>({
  locale: defaultLocale,
  t: (key, params) => translate([enCatalog], defaultLocale, key, params),
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const overrides = useI18nOverrides(locale);
  const [chain, setChain] = useState<readonly Catalog[]>([enCatalog]);

  useEffect(() => {
    if (locale === defaultLocale) {
      setChain([enCatalog]);
      return;
    }
    // English renders until the locale's chunk lands, so first paint is a real language rather
    // than a set of keys. `live` drops a chunk that arrives after the locale changed again.
    let live = true;
    void loadChain(locale).then((next) => {
      if (live) setChain(next);
    });
    return () => {
      live = false;
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = getDirection(locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    // The database layer sits on top of the bundles, and only when there is something in it: an
    // empty, failed or still-loading override fetch leaves the chain exactly as it was, which is
    // what makes a broken endpoint cost a correction rather than a screen (POS2-1133).
    const corrections = overrides.data?.length ? [overridesToCatalog(overrides.data)] : [];
    const merged = [...corrections, ...chain];
    return { locale, t: (key, params) => translate(merged, locale, key, params) };
  }, [chain, locale, overrides.data]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
